<template>
    <section id="cloud-plan" class="cloud-plan" >
        <section id='sub-part' name='subscribe'>
            <lock-location-duration-selector
                :storageLocList='storageLocList'
                :cloudProductLists='cloudProductLists'
                @changeSelect='handleLocationChange'
                @changeDuration='handleDurationChange'
                :hideCountryDialog="isUpdatePlan"
                :dataPlanSeries="currentDataPlanSeries"
                :durationList="durationList"
            />
        </section>

        <section class="plans-select" id="plans-select">
            <div>
                <div class='legacy_tip_container' v-if='isLegacyPlanOwner'>
                    <div class='legacy_tip'>
                        <span class='icon-icon-hint-warning'></span>
                        <span class='tip_content' v-html="$t('homepage.legacy_tip', lang, { link: config.legacy_plan_support })"></span>
                    </div>
                </div>
                <RichText class="country-text" v-if="countryPromotionText" :html="countryPromotionText"/>
                <div
                    v-if="deviceGroupList.length>0 && showPlansInfo"
                    class="plan-content"
                    :class="[
                        { tipAbove: !isLegacyPlanOwner },
                        deviceGroupList.length > 1 ? 'plan-content--carousel' : 'plan-content--single'
                    ]"
                    :key='currentDuration.key'
                >
                    <carousel
                        ref="planCarousel"
                        :mouseDrag="false"
                        :items="carouselItems"
                        :key='locationKey+currentDuration'
                        :margin="24"
                        v-if="deviceGroupList.length>1"
                        @ready="onCarouselReady"
                        contained
                    >
                        <lock-device-plans v-for="(deviceGroup, index) in deviceGroupList"
                            :key="index"
                            :deviceGroupInfo="deviceGroup"
                            :subscription="sub"
                            :activeItem="activeItem"
                            :listIndex="index"
                            :promotions="promotions"
                            @changeActive="changeActive"
                            :show-ai-info="showAiInfo"
                            :current-duration="currentDuration"
                            :cloudProductLists="cloudProductLists"
                            :isUpdatePlan="isUpdatePlan"
                            :isOtherRegion="isOtherRegion"
                            :displayOnly="displayOnly"
                            @goSubscription="goCheckout"
                        />
                    </carousel>
                    <template v-else>
                        <lock-device-plans v-for="(deviceGroup, index) in deviceGroupList"
                            :key="index"
                            :deviceGroupInfo="deviceGroup"
                            :subscription="sub"
                            :activeItem="activeItem"
                            :listIndex="index"
                            :promotions="promotions"
                            @changeActive="changeActive"
                            :show-ai-info="showAiInfo"
                            :current-duration="currentDuration"
                            :cloudProductLists="cloudProductLists"
                            :isUpdatePlan="isUpdatePlan"
                            :isOtherRegion="isOtherRegion"
                            :displayOnly="displayOnly"
                            @goSubscription="goCheckout"
                        />
                    </template>
                </div>

                <div class='bottom-wrapper' :class="{isMobile}">
                    <section class='basic-plan-container'>
                        <div class='show-basic-plan-container'>
                            <a v-if='basicSerial.plans && isNewCustomer' class="ui-link showBasicBtn" @click='isExpand = !isExpand'>
                                <span class='text'>{{ $t('homepage.show_basic_plan') }}</span><span class="icon" v-if='!isExpand'>+</span><span class="icon" v-if='isExpand'>-</span>
                            </a>
                        </div>
                        <div class="basic-plan" v-if="basicSerial.plans && isNewCustomer && isExpand">
                            <basic-plan-card
                                :key='locationKey'
                                :planTitle="basicSerial.i18n.name"
                                :cloudProductLists="cloudProductLists"
                                type="new"
                                :loc="isUpdatePlan ? 'update' : 'subscribe'"
                                :locationKey='locationKey'
                                :modelKey="currentDuration.key"
                                :basicSerial="basicSerial"
                                :showSubscribe="!isOtherRegion"
                                :displayOnly="displayOnly"
                                @subscribe="goCheckout"
                            />
                        </div>
                    </section>
                </div>
            </div>
        </section>
        <email-verify-dialog v-model="showEmailVerifyDialog" @verified="goCheckout"/>
        <reo-dialog
            v-if="showOtherRegionDialog"
            @cancel="showOtherRegionDialog = false"
        >
            <div class="dialog-content">
                {{ $t('subscribe.not_available_common') }}
            </div>
        </reo-dialog>
    </section>

</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import CloudLogo from 'IMAGE/homepage/cloud-logo.png';
import IntroImg from 'IMAGE/homepage/homepage-introducing.png';
import { config } from 'CONFIG';
import { isInVisibleArea } from '@/common/js/utils';
import { listToString } from '@/api/s3Config';
import { unfoldError } from '@/common/js/notice';
import { getCloudProductLists, getStorageRegionByCountry } from '@/api/reolink/cart';
import paymentMethods from '@/config/paymentMethods';
import Carousel from '@/basic/Carousel.vue';
import nprogress from 'nprogress';
import { checkNewCustomer, getTotalSubs, ownLegacyPlan } from '@/api/dashboard';
import { ensureGdprCookiesByCountry } from '@/common/js/gdprCookie';
import { getPromotionItems } from '@/api/order';
import { fetchAllPages } from '@/utils/request';
import { Async } from '@reolink-web/utils';
import {
    currStage,
    ESeriesCategory,
    IGetCloudProductsResponse,
    IOrder,
    IProductData,
    IPromotionItem,
    paymentStatus
} from '@reolink-cloud/interface';
import { getCountryPromotionText, getCountryWithoutOther, getDeviceGroupWithPlans, getShowAiInfo } from '@/utils/business';
import {
    IDeviceGroupWithPlans,
    IDeviceIdentity, IDuration, IGoCheckoutArgs, IProductPageArgs, ISeriesItem,
    ISubscriptionWithPlanInfo, ITotalDeviceWithSubInfo
} from '@/components/PlanSubscribeSection/lib/types';
import { getDeviceTotal } from '@/api/cloud';
import { getSubscriptions } from '@/api/subscriptions';
import { getOrders, getPlanById } from '@/api/cart';
import dayjs from 'dayjs';
import * as _ from '@reolink-web/utils';
import { getPlansByDuration } from '@/components/PlanSubscribeSection/lib/utils';
import BasicPlanCard from '@/components/PlanSubscribeSection/components/BasicPlanCard.vue';
import LockLocationDurationSelector from 'VIEW/user/Subscribe/LockPlan/components/LockLocationDurationSelector.vue';
import LockDevicePlans from 'VIEW/user/Subscribe/LockPlan/components/LockDevicePlans.vue';
import { IProductSeries } from '@/interface';
import EmailVerifyDialog from '@/components/EmailVerifyDialog.vue';
import ReoDialog from '@/basic/Dialog.vue';
import { productsManager } from '@/lib';
import { getPlanBlockList } from '@/api/plan';
import { duplexPair } from 'stream';

export default Vue.extend({
    components: {
        LockDevicePlans,
        Carousel,
        LockLocationDurationSelector,
        BasicPlanCard,
        EmailVerifyDialog,
        ReoDialog

    },
    props: {
        productsArgs: Object as PropType<IProductPageArgs>,
        sub: Object as PropType<ISubscriptionWithPlanInfo | null>,
        deviceIdentity: { type: Object as PropType<IDeviceIdentity>, required: true },
        displayOnly: { type: Boolean, default: false },
    },

    data () {

        return {

            paymentMethods,
            config,
            discount: 0,
            activeItem: 100,
            carouselItems: 3,
            scrollHandleEnable: true,
            mobileSubBtnShow: false,
            basicSerial: {} as Record<string, any>,
            isShowTitle: true,
            cloudProductLists: {} as Record<string, any>,
            storageLocList: {} as Record<string, any>,
            locationKey: '',
            currentDuration: {} as IDuration,
            userSelectLoc: {} as Record<string, any>,
            supportedDeviceList: [] as any[],
            supportedCountryList: [] as any[],
            planNoticeText: '',
            count: 0,
            showAvailableDevices: false,
            supportedTrafficDeviceList: [] as any[],
            isNewCustomer: true,
            CloudLogo,
            IntroImg,
            legacySubs: [] as any[],
            isExpand: false,
            promotions: [] as IPromotionItem[],
            ipCountry: config.fallbackCountry,
            showAiInfo: false,
            showDevices: false,
            deviceData: [] as ITotalDeviceWithSubInfo[],
            orders: [] as IOrder[],
            currentDataPlanSeries:[] as IProductSeries[],
            showPlansInfo:false,
            readyEmitted: false,
            showEmailVerifyDialog: false,
            args: null as null | IGoCheckoutArgs,
            isOtherRegion: false,
            showOtherRegionDialog: false,
            blockPlanIds: [] as string[],
            allDeviceGroupList: [] as IDeviceGroupWithPlans[],
        };

    },

    mounted () {

        window.addEventListener( 'scroll', this.scrollHandle );

    },

    methods: {
        carouselInit () {

            if ( this.isMobile ) {

                this.carouselItems = 1;

            }
            else if ( this.deviceGroupList.length < 3 ) {

                this.carouselItems = this.deviceGroupList.length;

            } else {

                this.carouselItems = 2;

            }

            this.$nextTick(() => {

                if ( this.deviceGroupList.length > 1 ) {

                    ( this.$refs.planCarousel as any )?.init();

                }

            });

        },
        onCarouselReady () {

            if ( !this.readyEmitted ) {

                requestAnimationFrame(() => {

                    if ( !this.readyEmitted ) {

                        this.readyEmitted = true;
                        this.$emit( 'ready' );

                    }

                });

            }

        },
        async getPlans () {

            try {

                let data: Record<string, any> = {};
                let lang = this.$i18n.locale;

                const includeNotificationPlan = this.$route.query.includeNotificationPlan === 'true';
                const includedCategories = [ESeriesCategory.CLOUD_STORAGE_DATA_PLAN];
                data = await getCloudProductLists( lang, this.country, this.productsArgs, includeNotificationPlan, includedCategories );

                ensureGdprCookiesByCountry( this.country, this.$i18n.locale );
                if ( this.displayOnly ) {

                    this.isNewCustomer = true;
                    this.legacySubs = [];

                } else {

                    let { subs, activeSubs } = await getTotalSubs();
                    this.isNewCustomer = checkNewCustomer( subs );
                    this.legacySubs = ownLegacyPlan( activeSubs );

                }

                if ( this.client !== 'reolink' ) {

                    this.isNewCustomer = false;

                }
                this.cloudProductLists = data.cloudProductLists;
                this.ipCountry = data.country;
                this.storageLocList = data.storageLocList;
                this.currentDataPlanSeries = this.findItemsWithCategory( data.cloudProductLists.planSeries, 'cloud_data_plan' );
                this.supportedTrafficDeviceList = data.supportedTrafficDeviceList;
                this.count++;

            } catch ( err ) {

                console.error( err );
                unfoldError( 'errors.server_error' );

            }

        },

        findItemsWithCategory( planSeries: Record<string, IProductSeries>, category: string, country?: string ):  IProductSeries[] {

            const result: IProductSeries[] = [];

            for ( const key in planSeries ) {

                const currentSeries = planSeries[key];
                const currentCountry = country ?? this.country;
                if ( planSeries[key].category === category
                && currentSeries.supportedCountryList?.some( countryInfo => countryInfo.code === currentCountry )
                ) {

                    result.push( planSeries[key] );

                }

            }

            return result;

        },

        changeActive ( index ) {

            this.activeItem = index;

        },

        goCheckout (  args?: IGoCheckoutArgs ) {

            if ( args ) {

                this.args = args;

            }
            if ( !this.verified ) {

                this.showEmailVerifyDialog = true;
                return;

            }

            this.$emit( 'goCheckout', {
                p_id: this.args?.plan.id,
                isCurrentSubscriptionPlan: this.args?.isCurrentSubscriptionPlan,
                ...( this.args?.fromPromotionRichText ? { apply_promotion_usage_limit: '1' } : {})
            });

        },

        scrollHandle ( ) {

            if ( !this.scrollHandleEnable ) {

                return false;

            }
            this.scrollHandleEnable = false;
            setTimeout(() => {

                if ( $( 'body' ).eq( 0 )[ 0 ].offsetWidth < 1200 ) {

                    this.calcPlanIsInWindow();

                }
                this.scrollHandleEnable = true;

            }, 100 );

        },

        calcPlanIsInWindow () {

            let visible = isInVisibleArea( '#plans-select' );
            this.mobileSubBtnShow = visible;

        },

        jumpToPlan () {

            document.querySelector( '#sub-part' )!.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });

        },
        handleDurationChange ( val: IDuration ) {

            this.currentDuration = val;

        },
        handleLocationChange ( val ) {

            // 请求不同的套餐
            this.locationKey = val.locationKey;
            this.supportedDeviceList = val.supportedDeviceList;
            this.supportedCountryList = val.supportedCountryList;
            if ( val.list.length > 0 ) {

                if ( this.isNewCustomer ) {

                    this.basicSerial = val.basicSerial ?? {};

                } else {

                    for ( const [key, deviceGroupInfo] of Object.entries<IDeviceGroupWithPlans>( this.storageLocList[val.locationKey]?.deviceGroupsList ?? {})) {

                        if ( deviceGroupInfo.basicSerial ) {

                            const basicSerial = {
                                ...deviceGroupInfo.basicSerial,
                                products: deviceGroupInfo.basicSerial.plans
                            };

                            ( this.storageLocList[this.locationKey].deviceGroupsList[key] as IDeviceGroupWithPlans ).planList = [
                                ...( this.storageLocList[this.locationKey].deviceGroupsList[key] as IDeviceGroupWithPlans ).planList,
                                basicSerial
                            ];

                        }

                    }

                }

            } else {

                this.basicSerial = {};

            }
            this.planNoticeText = typeof this.cloudProductLists.storageRegions[this.locationKey].i18n.note !== 'undefined' ? this.cloudProductLists.storageRegions[this.locationKey].i18n.note : '';

        },
        goAnchor ( id ) {

            document.querySelector( `#${id}` )!.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });

        },
        async getIfShowAiInfo() {

            this.showAiInfo = await getShowAiInfo( this.country );

        },
        async initDeviceAndSubs() {

            const [deviceDataRaw, { items: subs }] = await Promise.all( [
                getDeviceTotal(),
                getSubscriptions()
            ] );
            const deviceData = deviceDataRaw as ITotalDeviceWithSubInfo[];
            for ( let device of deviceData ) {

                for ( let sub of subs ) {

                    if ( device.subscription === sub.id ) {

                        device.subName = sub.title;
                        if ( typeof this.cloudProductLists.plans[sub.plan] === 'undefined' ) {

                            let planDetails = await getPlanById( sub.plan, this.lang );
                            device.planTitle = planDetails.series.i18n.name;
                            this.cloudProductLists.plans[`${sub.plan}`] = planDetails.plan;
                            this.cloudProductLists.plans[`${sub.plan}`].title = planDetails.series.i18n.name;
                            this.cloudProductLists.planSeries[`${planDetails.plan.seriesKey}`] = planDetails.series;

                        } else {

                            device.planTitle = this.cloudProductLists.plans[sub.plan].title;

                        }

                    }

                }
                if ( typeof device.modelTitle === 'undefined' ) {

                    Object.keys( this.cloudProductLists.deviceModels ).map( key => {

                        if ( this.cloudProductLists.deviceModels[key].title === device.model ) {

                            device.modelTitle = this.cloudProductLists.deviceModels[key].title;

                        }

                    });

                }

            }
            this.deviceData = deviceData;

        },
        async getOrders() {

            const orders = await getOrders( 1, 100, dayjs().valueOf(), dayjs().subtract( 1, 'day' ).valueOf());
            this.orders = orders.items;

        },
        getDeviceGroupPlans( country?: string ): IDeviceGroupWithPlans[] {

            // 流量套餐
            const dataPlanList: ISeriesItem[] = this.currentDataPlanSeries.map(( planSeries ) => {

                return {
                    seriesName: planSeries.i18n.name,
                    products: ( planSeries.plans ?? [] ).filter( plan => !this.blockPlanIds.includes( plan.id )),
                    sortOrder: planSeries.style['series.sort'] ?? 0
                };

            });
            const dataPlan: IDeviceGroupWithPlans = {
                planList: dataPlanList,
                title: '',
                des: '',
                models: [],
                key: '',
                sort: 1
            };

            //合并套餐
            const combinePlan = _.Object.deepClone( getDeviceGroupWithPlans( this.cloudProductLists as IProductData['cloudProductLists'], { country:country ?? this.country, category: ESeriesCategory.CLOUD_STORAGE_DATA_PLAN, legacyTag: this.sub?.legacyTag })).reduce<IDeviceGroupWithPlans>(( acc, cur ) => {

                if ( this.displayOnly || cur.models?.some( m => m.key === this.curDevice?.model )) {

                    acc.planList = acc.planList.concat( cur.planList.filter( plan => !this.blockPlanIds.includes( plan.id )));

                }

                return acc;

            }, {
                planList: [],
                title: '',
                des: '',
                models: [],
                key: '',
                sort: 0
            });
            const list: IDeviceGroupWithPlans[] = [combinePlan, dataPlan];
            for ( const deviceGroup of list ) {

                for ( const series of deviceGroup.planList ) {

                    series.products = series.products.filter( plan => {

                        /**
                         * 只允许订阅一次的套餐有在支付中的订单就不显示
                         */
                        for ( const order of this.orders ) {

                            if ( order.status.currStage === currStage.PAYMENT &&
                                order.status.paymentStatus === paymentStatus.CREATED &&
                                order.items[0].productType === 'storage_plan' &&
                                order.items[0].context.action !== 'renew'
                            )
                                if ( order.items[0].productId === plan.id && plan.limitations.userMaxSubscribeTimes === 1 ) {

                                    return false;

                                }

                        }

                        if ( plan.status === 'renew_only' ) {

                            return false;

                        }

                        if ( this.$route.query.support_ai ) {

                            if ( !config.aiFeatures.some( item => plan.style[`cloud_ai.${item}`] )) {

                                return false;

                            }

                        }

                        if ( this.isUpdatePlan ) {

                            if ( parseFloat( plan.pricing.salePrice ) === 0 ) {

                                return false;

                            }

                            /**
                             * 切换套餐页面不显示不可以切换的套餐
                             */
                            if ( this.sub?.plan.id !== plan.id ) {

                                return plan.limitations.switchFromOther;

                            }

                        }
                        return true;

                    });

                }

            }

            return list;

        }

    },

    computed: {
        currentLang () {

            return this.$store.getters.lang;

        },

        loginDashboardUrl () {

            return config.accountCenterLoginUrl + '?redirect=' + window.location.origin + '/user/cloud-library/';

        },

        signupUrl () {

            return config.accountCenterLoginUrl + '?redirect=' + window.location.origin + '/user/cloud-library/#signup';

        },

        userId(): boolean {

            return this.$store.getters.userId;

        },

        isMobile(): boolean {

            return this.$store.getters.isMobile;

        },

        lang(): string  {

            return this.$store.getters.lang;

        },

        device (): string {

            let deviceList = this.supportedDeviceList;
            if ( !deviceList?.length ) {

                return '';

            }
            return listToString( deviceList.map( item => item.product ));

        },

        countrySupported (): string {

            let countrySupported = this.supportedCountryList;
            if ( !countrySupported?.length ) return '';
            let locale = this.lang;
            let texts = countrySupported.map( item => item[ locale ] );
            if ( !texts?.length ) {

                return '';

            }
            return listToString( texts );

        },
        userStatus (): string {

            return this.$store.state.user.status;

        },
        client (): string {

            return this.config.oem;

        },
        isLegacyPlanOwner (): boolean {

            return Boolean( this.sub?.legacyTag );

        },
        country(): string {

            return this.$store.getters.country;

        },
        isCountryAiOpen(): boolean {

            return config.aiFeatures.length > 0;

        },
        curDevice(): ITotalDeviceWithSubInfo | null {

            return this.deviceData.find( item => item.uid === this.deviceIdentity.uid ) ?? null;

        },
        deviceGroupList(): IDeviceGroupWithPlans[] {

            return this.allDeviceGroupList.filter( item => getPlansByDuration( item.planList, this.currentDuration ).length > 0 );

        },

        isUpdatePlan(): boolean {

            return this.productsArgs?.page === 'update-plan';

        },

        hasAvailablePlansAtLocation(): boolean {

            return this.deviceGroupList.length !== 0;

        },

        countryPromotionText(): string | undefined {

            return getCountryPromotionText( this.promotions, getPlansByDuration( this.deviceGroupList.reduce<ISeriesItem[]>(
                ( acc, group ) => acc.concat(
                    ...group.planList
                ),
                []
            ), this.currentDuration ));

        },

        verified (): boolean {

            return this.$store.state.user.user.email.status === 'verified';

        },
        durationList(): IDuration[] {

            let list: IDuration[] = [];

            for ( const planItem of this.allDeviceGroupList.map( item => item.planList.map( series => series.products )).flat( 2 )) {

                const amount = planItem.duration.amount;
                const unit = planItem.duration.unit;
                const key = `${amount}-${unit}`;
                if ( !list.some( item => item.key === key ) && !planItem.seriesKey.includes( 'basic' )) {

                    list.push({
                        key,
                        title: this.$tc( `cycle.${unit}`, amount === 1 ? 1 : 0, { count: amount }),
                        id: key,
                        amount,
                        unit
                    });

                }

            }

            const unitToOrder = {
                year: 0,
                month: 3,
                week:2,
                day: 1
            };
            list = list.sort(( a, b ) => {

                if ( a.unit === b.unit ) {

                    return a.amount - b.amount;

                } else {

                    return unitToOrder[a.unit] - unitToOrder[b.unit];

                }

            });

            return list;

        },

    },

    watch: {

        userStatus: {

            async handler ( newVal ): Promise<void> {

                if (( newVal === 'logined' || newVal === 'notLogined' ) && this.count < 1 ) {

                    const promises = [
                        this.getPlans(),
                        this.getIfShowAiInfo(),
                    ];
                    if ( !this.displayOnly ) {

                        promises.push( this.getOrders());

                    }
                    await Promise.all( promises );
                    if ( !this.displayOnly ) {

                        await this.initDeviceAndSubs();

                    }
                    await productsManager.update({
                        lang: this.lang,
                        visible_only: false
                    });

                    if ( window.location.href.includes( 'rich-notification' )) {

                        this.$nextTick(() => {

                            this.goAnchor( 'rich-notification' );

                        });

                    }

                    this.allDeviceGroupList = this.getDeviceGroupPlans();
                    this.$nextTick(() => {

                        this.showPlansInfo = true;
                        nprogress.done();

                    });

                }

            },

            immediate: true,
        },
        showPlansInfo() {

            if ( !this.hasAvailablePlansAtLocation ) {

                this.isOtherRegion = true;
                if ( !localStorage.getItem( 'hideLockCountryDialog' )) {

                    this.showOtherRegionDialog = true;
                    localStorage.setItem( 'hideLockCountryDialog', 'true' );

                }
                this.currentDataPlanSeries = this.findItemsWithCategory( this.cloudProductLists.planSeries, 'cloud_data_plan', config.fallbackCountry );
                this.allDeviceGroupList = this.getDeviceGroupPlans( config.fallbackCountry );

            }

        },
        isMobile () {

            this.carouselInit();

            if ( this.currentDuration ) {

                this.handleDurationChange( this.currentDuration );

            }

        },
        deviceGroupList () {

            this.$nextTick(() => {

                this.carouselInit();
                if ( this.deviceGroupList.length <= 1 ) {

                    requestAnimationFrame(() => {

                        if ( !this.readyEmitted ) {

                            this.readyEmitted = true;
                            this.$emit( 'ready' );

                        }

                    });

                }

            });

        },
        async curDevice ( newVal: ITotalDeviceWithSubInfo ) {

            if ( this.displayOnly ) return;

            if ( newVal ) {

                try {

                    [this.promotions, { planIds: this.blockPlanIds }] = await Promise.all( [
                        await fetchAllPages( getPromotionItems, {
                            page: 1,
                            limit: 100,
                            lang: this.lang,
                            country: getCountryWithoutOther( this.country ),
                            firstChargeData: {
                                device: {
                                    uid: this.deviceIdentity.uid,
                                    planCategories: [ ESeriesCategory.CLOUD_STORAGE_DATA_PLAN, ESeriesCategory.CLOUD_DATA_PLAN]
                                },
                            }

                        }),
                        getPlanBlockList({
                            uid: this.deviceIdentity.uid,
                        })
                    ] );

                } catch ( e ) {

                    console.error( 'get promotion error', e );

                }

            }

        },
        durationList () {

            this.currentDuration = this.durationList[0];

        }

    },

    destroyed () {

        window.removeEventListener( 'scroll', this.scrollHandle );

    }

});

</script>

<style scoped rel="stylesheet/css" lang="less">

:target:before,
.hash-target{
    display: block;
    height: 1.5rem;
    margin: -1.5rem 0 0;
    cursor: auto;
}

.unique-plan {
    background-color: #F7F8F9;
    padding: 0 .6rem .4rem;
    max-width: 12rem;
    margin:0 auto;
    box-sizing: border-box;
    @media @pc{
        padding-top: .3rem;
    }
}

.basic-plan{
    max-width: 12rem;
    margin:0 auto;
    box-sizing: border-box;
    padding-top: 16px;
    @media @mobile{
        padding-top: 20px;
    }
}
.cloud-plan{
    margin: 0 auto;
    max-width: 1000px;
    .mobile({
        .mobileMaxWidth();
    });
}
.plans-select{
    margin: 0 auto;
    max-width: 1000px;
    box-sizing: border-box;
    padding-top: 20px;
    padding-bottom: 80px;

    .titleBox{
        .title{
            font-weight: 500;
            font-size: 0.2rem;
            line-height: 1.4;
            color:#333333;
            @media @mobile{
                font-size: .28rem;
                text-align: left;
                padding: 0 20px .16rem;
            }
        }
        .notice-for-free{
            padding-top: 0.1rem;
            color:#333333;
            font-size: .16rem;
            line-height: 1.4;
            .des{
                margin-top: 0.08rem;
                display: block;
            }
            @media @mobile{
                padding: 0 20px;
                // margin-bottom:.32rem;
                text-align: left;
                margin: 0;
                +.plan-content{
                    margin-top: .32rem;
                }
            }
        }
    }
    .legacy_tip_container{
        font-size: 18px;
        @media @pc{
            margin-bottom:24px;
            padding-top: 24px;
        }
        @media @mobile{
            margin: 0;
            font-size: 14px;
        }
        .legacy_tip{
            padding: 14px 20px;
            text-align: left;
            background: @b_1;
            border-radius: 16px;
            display: flex;
            .icon-icon-hint-warning{
                color:#C15400;
                @media @pc{
                    font-size: .3rem;
                }
            }
            .tip_content{
                color:#333;
                font-weight: 500;
                margin-left:.2rem;
                /deep/ .support-link{
                    font-weight: 500;
                    color: @b_6;
                    cursor: pointer;
                    text-decoration: underline;
                    text-decoration-thickness:1px;
                }
                /deep/ a{
                    font-weight: 500;
                }
            }

        }

    }
    .country-text{
        font-size: 14px;
        color: #FF894A;
        font-weight: 600;
        text-align: center;
        @media @pc{
            margin-bottom:24px;
        }
        @media @mobile{
            margin-bottom:20px;
        }
    }
}

.plan-content{
    @media @pc{
        &.pc{
            .subscription-button{
                margin: 0 auto;
                margin-top: 40px;
            }
        }
    }
    @media @mobile{
        padding: 0;
        margin-top: 0.64rem;
        &.tipAbove{
            margin-top: .48rem;

        }
        .subscription-button-mobile{
            text-align: center;
            position: fixed;
            bottom: 0;
            left: 0;
            z-index: 3;
            width: 100%;
            /deep/ .reo-button-inner{
                border-radius: 0;
            }
        }
    }

    /*
     * subgrid 父网格：6 行与 LockDevicePlans 行槽对齐。
     * Carousel 路径建在 .owl-stage 上；单卡路径建在 .plan-content 自身。
     */
    &.plan-content--single{
        display: grid;
        grid-template-rows: repeat(6, auto);
        grid-auto-flow: column;
        grid-auto-columns: minmax(0, 1fr);
        align-items: stretch;
        justify-items: stretch;
    }

    &.plan-content--carousel{
        /deep/ .owl-stage{
            display: grid !important; // 覆盖 Carousel.vue 的 display:flex
            grid-template-rows: repeat(6, auto);
            grid-auto-flow: column;
            align-items: stretch;
            // owl 清浮动伪元素会多占一格，打断 subgrid 行轨
            &::after{
                content: none;
                display: none;
            }
        }
        /deep/ .owl-item{
            display: grid;
            grid-template-rows: subgrid;
            // 勿写 1 / -1：Less 会把 / 当除法算成 -1
            grid-row: span 6;
            float: none;
            height: auto;
        }
        /deep/ .lock-plan-card{
            height: auto;
            max-width: none;
            grid-row: span 6;
        }
    }

    /deep/ .lock-plan-card{
        box-sizing: border-box;
        @media @mobile{
            &, &.recommend{
                width: 100%;
            }
        }
    }
}

.bottom-wrapper{
    display: flex;
    flex-direction: column;
    padding-top: 16px;

    .plan-bottom{
        padding-top: 16px;
        margin-top: 16px;
        border-top: 1px solid @line;

        @media @mobile{
            text-align: left;
            padding-top: 12px;
            margin-top: 20px;
        }
    }
}

.show-basic-plan-container{
    width: 100%;
    text-align: left;
    margin-top: 0;
    line-break: 1.2;
    @media @mobile{
        width: unset;
        padding-top: 20px;
    }

    .showBasicBtn{
        font-weight: 500;
        font-size: .16rem;
        @media @mobile{
            font-size:16px;
        }
        color:#333;
        cursor: pointer;
        span:last-child{
            margin-left: 8px;
        }
    }
}

#sub-part{
    margin:0 auto;

    .title{
        color: #333;
        font-size: .4rem;
        line-height: 1.2;
        padding-top: .8rem;
        margin-bottom: .6rem;
        text-align: center;
    }
}

.buttons{
    display: flex;
    justify-content: center;
}

.dialog-content{
    font-size: 16px;
}

</style>
